import { clip, hiddenText, redact, redactBody, redactQuery, redactText } from '../../shared/redact';
import { getBases } from './base';
import { ownSelector, srcPrefix } from './config';
import { piiSelector, visibleText } from './elementText';
import { installKnownMask, learnFrom } from './known';
import { TraceBuffer, type TraceFile } from './trace';

export interface NetEntry {
  id: number;
  at: string;
  method: string;
  url: string;
  status: number | null;
  ms: number | null;
  ok: boolean | null;
  pending: boolean;
  error?: string;
  requestBody?: string;
  responseType?: string | null;
  responseBody?: string;
}

export interface ConsoleEntry {
  at: string;
  level: 'error' | 'warn' | 'uncaught' | 'unhandledrejection';
  message: string;
  stack?: string;
  source?: string;
}

export interface ActionEntry {
  at: string;
  type: 'click' | 'input' | 'key' | 'nav' | 'select';
  target?: string;
  key?: string;
  path?: string;
  delta?: number;
  via?: 'drag' | 'shift';
  /** Inside the host menu (`ownSelectors`): recorded, but never a scenario step. */
  own?: true;
}

const NET_MAX = 60;
const NET_FAILED_MAX = 20;
const CONSOLE_MAX = 50;
const ACTIONS_MAX = 60;
const REQUEST_BODY_MAX = 4_000;
const RESPONSE_BODY_MAX = 8_000;
const RESPONSE_READ_MAX = 32 * 1024;

const net: NetEntry[] = [];
const failed: NetEntry[] = [];
const consoleLog: ConsoleEntry[] = [];
const actions: ActionEntry[] = [];
let seq = 0;
let paused = false;
let installed = false;

type NetListener = (entry: NetEntry, startedAt: number) => void;
type NavListener = (path: string) => void;
const netListeners = new Set<NetListener>();
const navListeners = new Set<NavListener>();

export function onRecorderNet(fn: NetListener): () => void {
  netListeners.add(fn);
  return () => netListeners.delete(fn);
}

export function onRecorderNav(fn: NavListener): () => void {
  navListeners.add(fn);
  return () => navListeners.delete(fn);
}

function emitNet(entry: NetEntry, startedAt: number): void {
  for (const fn of netListeners) {
    try {
      fn(entry, startedAt);
    } catch {
    }
  }
}

function push<T>(ring: T[], item: T, max: number): void {
  ring.push(item);
  if (ring.length > max) ring.splice(0, ring.length - max);
}

/**
 * Дс1 (Quint recorder_actions; tracker reports 179, 180): the ring is mirrored into the tab's sessionStorage, cleaned
 * (`again`), and comes back after a reload; a sent report clears the mirror together with the ring.
 */
const ACTIONS_KEY = 'jaioraBugsActions';

function saveActions(): void {
  try {
    if (actions.length) window.sessionStorage.setItem(ACTIONS_KEY, JSON.stringify(actions.map(again)));
    else window.sessionStorage.removeItem(ACTIONS_KEY);
  } catch {
  }
}

function restoreActions(): void {
  try {
    const saved: unknown = JSON.parse(window.sessionStorage.getItem(ACTIONS_KEY) ?? '[]');
    if (!Array.isArray(saved)) return;
    const valid = saved.filter((entry): entry is ActionEntry => !!entry && typeof entry.at === 'string' && typeof entry.type === 'string');
    actions.unshift(...valid.slice(-ACTIONS_MAX));
    if (actions.length > ACTIONS_MAX) actions.splice(0, actions.length - ACTIONS_MAX);
  } catch {
  }
}

function pushAction(entry: ActionEntry): void {
  push(actions, entry, ACTIONS_MAX);
  saveActions();
}

function now(): string {
  return new Date().toISOString();
}

function shortUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    // The address goes into the snapshot: secret parameter values are hidden here, not by whoever reads it.
    return redactQuery(parsed.origin === window.location.origin ? `${parsed.pathname}${parsed.search}` : parsed.href);
  } catch {
    return redactQuery(url);
  }
}

function describeBody(body: BodyInit | null | undefined, url: string): string | undefined {
  if (body === undefined || body === null) return undefined;
  if (/\/api\/login\/?$/.test(url)) return '[скрыто: вход]';
  if (typeof body === 'string') {
    learnFrom(url, body);
    return redactBody(body, REQUEST_BODY_MAX, url);
  }
  if (body instanceof URLSearchParams) return redactBody(body.toString(), REQUEST_BODY_MAX, url);
  if (body instanceof Blob) return `[файл ${body.type || 'без типа'}, ${body.size} байт]`;
  if (body instanceof FormData) return `[FormData: ${[...new Set([...body.keys()])].join(', ')}]`;
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return `[двоичное, ${body.byteLength} байт]`;
  return '[тело другого вида]';
}

async function readHead(res: Response): Promise<string> {
  const reader = res.body?.getReader();
  if (!reader) return '';
  const decoder = new TextDecoder();
  let text = '';
  let size = 0;
  try {
    while (size < RESPONSE_READ_MAX) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      text += decoder.decode(value, { stream: true });
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  return text;
}

const TEXTUAL = /json|text|xml|javascript/;

let originalFetch: typeof fetch | null = null;

/** `fetch` past the recorder: the widget's own requests (config, uploads, report, settings, UX) are not in the snapshot. */
export function unrecordedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return (originalFetch ?? window.fetch.bind(window))(input, init);
}

/** The widget's own service, whoever calls it (the widget goes past the recorder; a host could call it directly). */
function isServiceUrl(url: string): boolean {
  try {
    return new URL(url, window.location.href).href.startsWith(`${getBases().bugs}/`);
  } catch {
    return false;
  }
}

function patchFetch(): void {
  const original = window.fetch.bind(window);
  originalFetch = original;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    // Skip the report's own uploads and the screenshot library's image fetches.
    if (paused || /^(data|blob):/.test(url) || isServiceUrl(url)) return original(input, init);
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const entry: NetEntry = {
      id: (seq += 1),
      at: now(),
      method,
      url: shortUrl(url),
      status: null,
      ms: null,
      ok: null,
      pending: true,
      requestBody: describeBody(init?.body, url),
    };
    push(net, entry, NET_MAX);
    const started = performance.now();
    try {
      const res = await original(input, init);
      entry.status = res.status;
      entry.ok = res.ok;
      entry.ms = Math.round(performance.now() - started);
      entry.pending = false;
      entry.responseType = res.headers.get('content-type');
      if (TEXTUAL.test(entry.responseType ?? '')) {
        void readHead(res.clone())
          .then((text) => {
            learnFrom(url, text);
            entry.responseBody = redactBody(text, RESPONSE_BODY_MAX, url);
          })
          .catch(() => undefined);
      }
      if (!res.ok) push(failed, entry, NET_FAILED_MAX);
      emitNet(entry, started);
      return res;
    } catch (err) {
      entry.pending = false;
      entry.ms = Math.round(performance.now() - started);
      entry.error = redactText(err instanceof Error ? `${err.name}: ${err.message}` : String(err));
      push(failed, entry, NET_FAILED_MAX);
      emitNet(entry, started);
      throw err;
    }
  };
}

function formatArg(arg: unknown): string {
  if (typeof arg === 'string') return redactText(arg);
  if (arg instanceof Error) return redactText(`${arg.name}: ${arg.message}`);
  try {
    return redactText(clip(JSON.stringify(redact(arg)) ?? String(arg), 1_000));
  } catch {
    return redactText(String(arg));
  }
}

function patchConsole(): void {
  for (const level of ['error', 'warn'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        const error = args.find((arg): arg is Error => arg instanceof Error);
        push(
          consoleLog,
          {
            at: now(),
            level,
            message: clip(args.map(formatArg).join(' '), 2_000),
            stack: error?.stack ? clip(redactText(error.stack), 3_000) : undefined,
          },
          CONSOLE_MAX,
        );
      } catch {
        // Recording must never break the console.
      }
      original(...args);
    };
  }
  window.addEventListener('error', (event) => {
    push(
      consoleLog,
      {
        at: now(),
        level: 'uncaught',
        message: clip(redactText(event.message || 'ошибка без текста'), 2_000),
        stack: event.error instanceof Error && event.error.stack ? clip(redactText(event.error.stack), 3_000) : undefined,
        source: event.filename ? `${shortUrl(event.filename)}:${event.lineno}:${event.colno}` : undefined,
      },
      CONSOLE_MAX,
    );
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    push(
      consoleLog,
      {
        at: now(),
        level: 'unhandledrejection',
        message: clip(formatArg(reason), 2_000),
        stack: reason instanceof Error && reason.stack ? clip(redactText(reason.stack), 3_000) : undefined,
      },
      CONSOLE_MAX,
    );
  });
}

const INTERACTIVE =
  'button, a, input, select, textarea, label, summary, [role=button], [role=tab], [role=menuitem], [role=option], ' +
  '[role=checkbox], [role=switch], [role=link], th, li';

/**
 * The window an element is in, for the "in window" part of a record: the nearest dialog, or the host menu
 * (`ownSelectors`) around it — the menu itself names the window, the menu button does not.
 */
function windowOf(el: Element): Element | null {
  const dialog = el.closest('.modal, [role=dialog]');
  const own = ownSelector();
  const menu = own ? el.parentElement?.closest(own) ?? null : null;
  if (dialog && menu) return dialog.contains(menu) ? menu : dialog;
  return dialog ?? menu;
}

/** Host menu button or menu (`ownSelectors`): what opens the report and the watch. */
export function isHostOwn(target: EventTarget | null): boolean {
  const own = ownSelector();
  return !!own && target instanceof Element && !!target.closest(own);
}

/** Never includes field values. */
export function describeElement(node: EventTarget | Element | null): string {
  const start = node instanceof Element ? node : null;
  const el = start?.closest(INTERACTIVE) ?? start;
  if (!el) return '—';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls = [...el.classList]
    .slice(0, 3)
    .map((c) => `.${c}`)
    .join('');
  const role = el.getAttribute('role');
  const field = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
  const label =
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    (field ? (el as HTMLInputElement).placeholder || (el as HTMLInputElement).name : '') ||
    '';
  // Places with people's words give only the length. Cleaned before clipping: a clipped quote is no longer recognized.
  const raw = field ? '' : visibleText(el);
  const pii = piiSelector();
  const text = raw && (el.matches(pii) || el.closest(pii) || el.querySelector(pii)) ? hiddenText(raw) : redactText(raw);
  const parts = [`${tag}${id}${cls}${role ? `[role=${role}]` : ''}`];
  if (el instanceof HTMLInputElement && el.type && el.type !== 'text') {
    parts[0] += `[type=${el.type}]${el.type === 'checkbox' || el.type === 'radio' ? (el.checked ? '[checked]' : '') : ''}`;
  }
  if (label) parts.push(`«${clip(redactText(label), 60)}»`);
  if (text) parts.push(`"${clip(text, 80)}"`);
  if (el instanceof HTMLAnchorElement && el.href) parts.push(`→ ${shortUrl(el.href)}`);
  // Square brackets, not guillemets: guillemets in a record read as the element's own label (scenario.ts `labelOf`).
  const block = el.closest('[data-bug-block]');
  if (block) {
    const src = block.getAttribute('data-bug-src');
    parts.push(`[блок: ${block.getAttribute('data-bug-block')}${src ? ` · ${srcPrefix()}${src}` : ''}]`);
  }
  const dialog = windowOf(el);
  const dialogName = dialog?.getAttribute('aria-label') || dialog?.querySelector('h2, h3')?.textContent?.trim();
  if (dialogName) parts.push(`в окне «${clip(redactText(dialogName), 60)}»`);
  return parts.join(' ');
}

export const BUG_OWN_ATTR = 'data-bug-own';

let picking = false;

/** While the help button picks places on the page, clicks and moves there are the widget's, not the user's actions. */
export function setPicking(on: boolean): void {
  picking = on;
}

export function isOwn(target: EventTarget | null): boolean {
  return picking || (target instanceof Element && !!target.closest(`[${BUG_OWN_ATTR}]`));
}

/**
 * A control of the widget for the trace (reports 105–107): tag, classes and its label — never the text around it (the
 * panel holds what the person typed, with names in it).
 */
export function describeOwn(node: EventTarget | null): string {
  const start = node instanceof Element ? node : null;
  const el = start?.closest('button, a, input, select, textarea, [role=button], [role=switch], [role=tab]') ?? start;
  if (!el) return '—';
  const cls = [...el.classList]
    .slice(0, 2)
    .map((c) => `.${c}`)
    .join('');
  const label = el.getAttribute('aria-label') || el.getAttribute('title') || '';
  return `${el.tagName.toLowerCase()}${cls}${label ? ` «${clip(redactText(label), 60)}»` : ''}`;
}

/**
 * What the widget itself did (a frame started or cancelled, the shield switched, a shot removed or brought back, the
 * screen mode): a `widget` event in the running record and the quiet trace, not a step of the person on the page.
 */
export function noteWidgetEvent(what: string): void {
  if (typeof performance === 'undefined') return;
  const at = performance.now();
  const event = { type: 'widget' as const, target: what, own: 'widget' as const };
  trace?.buffer.push(at, event);
  quiet?.push(at, event);
}

function patchActions(): void {
  document.addEventListener(
    'click',
    (event) => {
      if (paused || isOwn(event.target)) return;
      pushAction({ at: now(), type: 'click', target: describeElement(event.target), ...(isHostOwn(event.target) ? { own: true as const } : {}) });
    },
    { capture: true, passive: true },
  );
  let lastInput = '';
  document.addEventListener(
    'input',
    (event) => {
      if (isOwn(event.target)) return;
      const target = describeElement(event.target);
      // One entry per field while typing, not per keystroke.
      const last = actions[actions.length - 1];
      if (last?.type === 'input' && lastInput === target) {
        last.at = now();
        saveActions();
        return;
      }
      lastInput = target;
      pushAction({ at: now(), type: 'input', target });
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    'keydown',
    (event) => {
      if (isOwn(event.target)) return;
      if (!['Enter', 'Escape', 'Tab'].includes(event.key) && !(event.key.length === 1 && (event.metaKey || event.ctrlKey))) return;
      const key = `${event.metaKey ? 'Meta+' : ''}${event.ctrlKey ? 'Ctrl+' : ''}${event.key}`;
      pushAction({ at: now(), type: 'key', key, target: describeElement(event.target), ...(isHostOwn(event.target) ? { own: true as const } : {}) });
    },
    { capture: true, passive: true },
  );
  // Дс2: a section switched by `#` (the tracker's `#projects`, `#ux`) is a step too; the page listeners hear only a new
  // path or query — a shot belongs to the page, not to its section.
  const where = () => `${window.location.pathname}${window.location.search}`;
  let lastPath = where();
  let lastHash = window.location.hash;
  const notePath = () => {
    const path = where();
    const hash = window.location.hash;
    if (path === lastPath && hash === lastHash) return;
    const moved = path !== lastPath;
    lastPath = path;
    lastHash = hash;
    const step = `${redactQuery(path)}${redactText(hash)}`;
    pushAction({ at: now(), type: 'nav', path: step });
    trace?.buffer.add(performance.now(), { type: 'nav', path: step });
    quiet?.add(performance.now(), { type: 'nav', path: step });
    if (!moved) return;
    for (const fn of navListeners) {
      try {
        fn(redactQuery(path));
      } catch {
      }
    }
  };
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history);
    history[method] = (...args: Parameters<History['pushState']>) => {
      original(...args);
      notePath();
    };
  }
  window.addEventListener('popstate', notePath);
  window.addEventListener('hashchange', notePath);
}

export function installBugRecorder(): void {
  installKnownMask();
  if (installed || typeof window === 'undefined') return;
  installed = true;
  if (typeof window.fetch === 'function') patchFetch();
  patchConsole();
  restoreActions();
  patchActions();
  patchQuietTrace();
}

export function noteSelection(delta: number, via: 'drag' | 'shift', where: Element | null = null): void {
  if (paused || delta === 0) return;
  const count = Math.abs(delta);
  const tail = count % 10;
  const tens = count % 100;
  const noun = tail === 1 && tens !== 11 ? 'сообщение' : tail >= 2 && tail <= 4 && (tens < 10 || tens >= 20) ? 'сообщения' : 'сообщений';
  const what = `${delta > 0 ? 'выделено' : 'снято'} ${count} ${noun} ${
    via === 'drag' ? 'протягиванием' : 'Shift+клик'
  }`;
  const block = where?.closest('[data-bug-block]');
  const src = block?.getAttribute('data-bug-src');
  const target = `${what}${block ? ` [блок: ${block.getAttribute('data-bug-block')}${src ? ` · ${srcPrefix()}${src}` : ''}]` : ''}`;
  pushAction({ at: now(), type: 'select', target, delta, via });
}

export function pauseBugRecorder(value: boolean): void {
  paused = value;
}

/** Second pass on output: a name learned later is hidden in what was recorded earlier too. */
function again<T extends object>(item: T): T {
  const out: Record<string, unknown> = { ...(item as Record<string, unknown>) };
  for (const [key, value] of Object.entries(out)) if (typeof value === 'string') out[key] = redactText(value);
  return out as T;
}

/** `again: false` — plain copies, the second pass is left to the caller (a report cleans them later, in pieces). */
export function recorded(options: { again?: boolean } = {}): { network: NetEntry[]; failedNetwork: NetEntry[]; console: ConsoleEntry[]; actions: ActionEntry[] } {
  const copy = <T extends object>(list: T[]) => list.map(options.again === false ? (item: T) => ({ ...item }) : again);
  const recent = new Set(net.map((entry) => entry.id));
  return {
    network: copy(net),
    // Failed requests already pushed out of the main ring.
    failedNetwork: copy(failed.filter((entry) => !recent.has(entry.id))),
    console: copy(consoleLog),
    actions: copy(actions),
  };
}

/** The newest recorded action (null — none yet): a report taken now owns it and everything before. */
export function lastAction(): ActionEntry | null {
  return actions[actions.length - 1] ?? null;
}

/**
 * A sent report owns the recorded clicks up to `upTo` (all without it); later ones go to the next report.
 * Requests and console stay — they are app state, not a person's steps.
 */
export function clearActions(upTo?: ActionEntry | null): void {
  actions.splice(0, upTo === undefined ? actions.length : upTo === null ? 0 : actions.indexOf(upTo) + 1);
  saveActions();
}

export function actionsSince(at: string): ActionEntry[] {
  return actions.filter((entry) => entry.at >= at).map(again);
}

interface TraceSession {
  buffer: TraceBuffer;
  startedIso: string;
  stoppedAt: number | null;
  off: (() => void) | null;
}
let trace: TraceSession | null = null;

export function elementPath(node: EventTarget | null): string {
  let el = node instanceof Element ? (node.closest(INTERACTIVE) ?? node) : null;
  const parts: string[] = [];
  while (el && el !== document.body && parts.length < 5) {
    const cls = [...el.classList].slice(0, 2).map((c) => `.${c}`).join('');
    parts.unshift(`${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}${cls}`);
    if (el.hasAttribute('data-bug-block') || el.tagName === 'MAIN') break;
    el = el.parentElement;
  }
  return parts.join(' > ');
}

/**
 * Mouse moves get coordinates only; an element is described only for events that pass the sampling. Events in the
 * widget (its panel, pill, frame) are kept with `own: 'widget'` and a bare description of the control (reports 105–107:
 * the log of the tool), not dropped.
 */
function traceListeners(buffer: TraceBuffer) {
  const describe = (node: EventTarget | Element | null) => redactText(describeElement(node));
  const onClick = (event: MouseEvent) => {
    if (paused) return;
    const own = isOwn(event.target);
    buffer.push(performance.now(), {
      type: 'click',
      x: Math.round(event.clientX),
      y: Math.round(event.clientY),
      vw: window.innerWidth,
      vh: window.innerHeight,
      target: own ? describeOwn(event.target) : describe(event.target),
      ...(own ? { own: 'widget' as const } : { path: redactText(elementPath(event.target)) }),
    });
  };
  const onMove = (event: MouseEvent) => {
    if (paused) return;
    const pressed = event.buttons > 0;
    const at = performance.now();
    if (!buffer.due(pressed ? 'drag' : 'move', at)) return;
    buffer.push(at, {
      type: 'move',
      x: Math.round(event.clientX),
      y: Math.round(event.clientY),
      ...(pressed ? { b: 1 as const } : {}),
      ...(isOwn(event.target) ? { own: 'widget' as const } : {}),
    });
  };
  const onScroll = (event: Event) => {
    if (paused) return;
    const at = performance.now();
    if (!buffer.due('scroll', at)) return;
    const el = event.target instanceof Element ? event.target : null;
    const own = isOwn(event.target);
    buffer.push(
      at,
      el
        ? { type: 'scroll', sx: Math.round(el.scrollLeft), sy: Math.round(el.scrollTop), target: own ? describeOwn(el) : describe(el), ...(own ? { own: 'widget' as const } : {}) }
        : { type: 'scroll', sx: Math.round(window.scrollX), sy: Math.round(window.scrollY) },
    );
  };
  return { onClick, onMove, onScroll };
}

export function startTrace(): void {
  clearTrace();
  if (typeof window === 'undefined') return;
  const buffer = new TraceBuffer(performance.now());
  const { onClick, onMove, onScroll } = traceListeners(buffer);
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('mousemove', onMove, { capture: true, passive: true });
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  trace = {
    buffer,
    startedIso: now(),
    stoppedAt: null,
    off: () => {
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('mousemove', onMove, { capture: true });
      document.removeEventListener('scroll', onScroll, { capture: true });
    },
  };
}

/*
 * The quiet trace (report 39): clicks with coordinates and element path, and rough mouse moves (2 a second) are always
 * recorded, without the «Запись» switch; a report takes what happened since the previous one. A ring of QUIET_MAX.
 */
export const QUIET_MAX = 600;
let quiet: TraceBuffer | null = null;

function patchQuietTrace(): void {
  quiet = new TraceBuffer(performance.now(), QUIET_MAX);
  const { onClick, onMove } = traceListeners(quiet);
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('mousemove', onMove, { capture: true, passive: true });
}

/** Clicks and moves since the previous report (null — none) and the mark to forget them by once it is sent. */
export function quietTrace(): { file: TraceFile | null; mark: number } {
  if (!quiet) return { file: null, mark: 0 };
  return { file: quiet.tail(performance.now(), Date.now(), { w: window.innerWidth, h: window.innerHeight }, redactText), mark: quiet.mark() };
}

/** What went with a sent report is not sent again; clicks made while the request ran stay for the next one. */
export function clearQuietTrace(mark?: number): void {
  quiet?.dropSent(mark ?? quiet.mark());
}

export function stopTrace(): void {
  if (!trace || trace.stoppedAt !== null) return;
  trace.off?.();
  trace.off = null;
  trace.stoppedAt = performance.now();
}

export function clearTrace(): void {
  stopTrace();
  trace = null;
}

export function traceStatus(): { recording: boolean; events: number; durationMs: number } | null {
  if (!trace) return null;
  const end = trace.stoppedAt ?? performance.now();
  return { recording: trace.stoppedAt === null, events: trace.buffer.events.length, durationMs: Math.round(end - trace.buffer.startedAt) };
}

export function traceFile(): TraceFile | null {
  if (!trace || trace.buffer.events.length === 0) return null;
  return trace.buffer.toFile(trace.stoppedAt ?? performance.now(), trace.startedIso, { w: window.innerWidth, h: window.innerHeight }, redactText);
}
